/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { DocumentDto } from '../models/DocumentDto';
import type { DraftConfigurationDto } from '../models/DraftConfigurationDto';
import type { DraftConfigurationListDto } from '../models/DraftConfigurationListDto';
import type { ProductBasePriceDto } from '../models/ProductBasePriceDto';
import type { SearchTermsQueryDto } from '../models/SearchTermsQueryDto';
import type { TokenDto } from '../models/TokenDto';
import type { TokenRequestDto } from '../models/TokenRequestDto';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class DebtorService {

    /**
     * @returns void
     * @throws ApiError
     */
    public static deleteDeleteDraftConfiguration({
        debtorCode,
        uuid,
    }: {
        debtorCode: string,
        uuid: string,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/debtors/{debtorCode}/draft-configurations/{uuid}',
            path: {
                'debtorCode': debtorCode,
                'uuid': uuid,
            },
            errors: {
                401: `Unauthorized`,
                403: `Access denied.`,
            },
        });
    }

    /**
     * @returns DraftConfigurationListDto The collection of draft configurations.
     * @throws ApiError
     */
    public static getShowDraftConfigurations({
        locale,
        page,
        pageSize,
        debtorCode,
        country,
        customer,
        searchTermsQuery,
        searchTermsCustomers,
        searchTerms,
    }: {
        /**
         * Locale
         */
        locale: string,
        /**
         * Page number
         */
        page: number,
        /**
         * Page size number
         */
        pageSize: number,
        debtorCode: string,
        /**
         * Country code
         */
        country?: string,
        /**
         * Customer id
         */
        customer?: number,
        /**
         * Search terms query string
         */
        searchTermsQuery?: string,
        /**
         * Search terms customer IDs
         */
        searchTermsCustomers?: Array<number>,
        searchTerms?: SearchTermsQueryDto,
    }): CancelablePromise<DraftConfigurationListDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/debtors/{debtorCode}/draft-configurations',
            path: {
                'debtorCode': debtorCode,
            },
            query: {
                'locale': locale,
                'country': country,
                'page': page,
                'pageSize': pageSize,
                'customer': customer,
                'searchTerms[query]': searchTermsQuery,
                'searchTerms[customers][]': searchTermsCustomers,
                'searchTerms': searchTerms,
            },
            errors: {
                401: `Unauthorized`,
                403: `Access denied.`,
            },
        });
    }

    /**
     * @returns any Successfully saved draft configuration.
     * @throws ApiError
     */
    public static postSaveDraftConfiguration({
        debtorCode,
        requestBody,
    }: {
        debtorCode: string,
        /**
         * Request body for creating a draft configuration.
         */
        requestBody: DraftConfigurationDto,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/debtors/{debtorCode}/draft-configurations',
            path: {
                'debtorCode': debtorCode,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
                403: `Access denied.`,
            },
        });
    }

    /**
     * @returns DocumentDto Returns an array of documents for the debtor.
     * @throws ApiError
     */
    public static getShowDebtorDocuments({
        locale,
        debtorCode,
    }: {
        /**
         * Locale
         */
        locale: string,
        debtorCode: string,
    }): CancelablePromise<Array<DocumentDto>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/debtors/{debtorCode}/documents',
            path: {
                'debtorCode': debtorCode,
            },
            query: {
                'locale': locale,
            },
            errors: {
                400: `File not found.`,
                401: `Unauthorized`,
            },
        });
    }

    /**
     * @returns ProductBasePriceDto Returns array of base price per product code.
     * @throws ApiError
     */
    public static getShowProductBasePrices({
        debtorCode,
        locale,
        country,
        products,
    }: {
        /**
         * Debtor code
         */
        debtorCode: string,
        /**
         * Locale
         */
        locale: string,
        /**
         * Country code
         */
        country: string,
        /**
         * Product codes
         */
        products: Array<string>,
    }): CancelablePromise<Array<ProductBasePriceDto>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/debtors/{debtorCode}/product-base-prices',
            path: {
                'debtorCode': debtorCode,
            },
            query: {
                'locale': locale,
                'country': country,
                'products[]': products,
            },
            errors: {
                401: `Unauthorized`,
                403: `Access denied.`,
            },
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public static deleteRemoveDebtorToken({
        debtorCode,
        uuid,
    }: {
        debtorCode: string,
        uuid: string,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/debtors/{debtorCode}/tokens/{uuid}',
            path: {
                'debtorCode': debtorCode,
                'uuid': uuid,
            },
            errors: {
                400: `Unable to remove debtor token`,
                401: `Unauthorized`,
            },
        });
    }

    /**
     * @returns TokenDto Returns an array of tokens for the debtor.
     * @throws ApiError
     */
    public static getShowDebtorTokens({
        debtorCode,
    }: {
        debtorCode: string,
    }): CancelablePromise<Array<TokenDto>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/debtors/{debtorCode}/tokens',
            path: {
                'debtorCode': debtorCode,
            },
            errors: {
                400: `Unable to retrieve debtor tokens`,
                401: `Unauthorized`,
            },
        });
    }

    /**
     * @returns any Debtor token saved.
     * @throws ApiError
     */
    public static postCreateDebtorToken({
        debtorCode,
        requestBody,
    }: {
        debtorCode: string,
        requestBody: TokenRequestDto,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/debtors/{debtorCode}/tokens',
            path: {
                'debtorCode': debtorCode,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Unable to save debtor token`,
                401: `Unauthorized`,
            },
        });
    }

}
